import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import TextField from "@/common/components/TextField";
import { zodResolver } from "@hookform/resolvers/zod";
import { QueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { createSchema, PermissionValidationSchema } from "@/features/permissions/schemas/create.schema";
import useCreatePermission from "../../hooks/useCreatePermission";
import { FormTransformers } from "@/common/utils/formTransformers";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import SelectController from "@/common/components/SelectController";
import { usePermissionsOperationKlasses } from "@/common/hooks/queries/usePermissionsOperationKlasses";
import TextArea from "@/common/components/TextArea";
import useDebounce from "@/common/hooks/useDebounce";
import Checkbox from "@/common/components/Checkbox";
import { ShowHeader } from "@/common/components/ShowHeader";

const Create = () => {
  const navigate = useNavigate();
  const queryClient = new QueryClient()

  const [messages, setMessages] = useState<string[] | boolean>(false)
  const [showOnlyWithoutPermmission, setShowOnlyWithoutPermmission] = useState(true);

  const [searchPermissionOperationKlass, setSearchPermissionOperationKlass] = useDebounce('');

  const {
    mutate,
    isPending
  } = useCreatePermission({
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['permissions']})
      navigate('/permissions')
    },
    onError: (error) => {
      if (error.response?.data.errors.base) {
        setMessages(error.response?.data.errors.base)
      } else {
        FormTransformers.errorsTransformer({ setError })(error)
      }
    }
  })

  const { data: operationKlassesOptions } = usePermissionsOperationKlasses({
    filters: { 
      search: searchPermissionOperationKlass,
      withoutPermission: showOnlyWithoutPermmission
    },
    mapper: (data: string[]) => data?.map((item) => ({ value: item, label: item }))
  });

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<PermissionValidationSchema>({
    resolver: zodResolver(createSchema)
  });

  const onSubmit: SubmitHandler<PermissionValidationSchema> = (data) => mutate(data);

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="false">
      <ShowHeader
        goBack
        goBackProps={{ fallback: '/permissions' }}
        titleClassName="font-normal text-lg text-white-400"
        subtitleClassName="font-normal text-lg"
        title='Permissões'
        subtitle='Nova permissão'
        buttons={(
          <div className="flex gap-4">
            <Link to='/permissions'>
              <Button variant='outline-error'>
                Cancelar
              </Button>
            </Link>

            <Button data-testid='create_permission' type="submit" className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]" isLoading={isPending}>
              Confirmar
            </Button>
          </div>
        )}
      />

      <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
        <div className="flex justify-between items-center">
          <p className="text-lg font-bold">
            Nova permissão
          </p>

          <Checkbox
            label="Mostrar classes de operação sem permissão vinculada"
            className="italic"
            checked={showOnlyWithoutPermmission}
            onChange={() => setShowOnlyWithoutPermmission(!showOnlyWithoutPermmission)}
          />
        </div>

        <div className="flex gap-2">
          <TextField
            label="Nome"
            markAsRequired
            errorMessage={errors?.display_name?.message}
            className="w-1/2"
            {...register('display_name')}
          />
          <SelectController
            name='operation_klass'
            control={control}
            label="Classe de operação"
            markAsRequired
            onInputChange={setSearchPermissionOperationKlass}
            options={operationKlassesOptions}
            errorMessage={errors?.operation_klass?.message}
            className="w-1/2"
          />
        </div>

        <TextField
          label="Descrição"
          markAsRequired
          errorMessage={errors?.description?.message}
          {...register('description')}
        />

        <TextArea
          label="Propiedades"
          placeholder="{}"
          {...register('properties')}
          errorMessage={errors.properties?.message || undefined}
          rows={8}
        />
      </Box>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </form>
  );
};

export default Create;
